import React, {useContext, useEffect, useRef, useState} from 'react';
import {Box, Button, Paper, Stack, TextareaAutosize} from '@mui/material';
import {ChatBubble} from "./ChatBubble";
import {Chat, Message} from "./Models";
import {Configuration} from "../../Configuration";
import {MarkDown} from "../../util/MarkDown";
import {AuthContext, IAuthContext} from "react-oauth2-code-pkce";
import {Helpers} from "../../util/Helpers";

interface Props {
    chat: Chat | undefined
}
const ChatMessageList  = ( { chat }: Props ) => {
    // const auth = useAuth();
    const {token}: IAuthContext = useContext(AuthContext)
    const baseUrl = `${Configuration.baseApiUrl}/chat`;
    const suggestions = [
        "Summarize the transcript of the last council meeting and translate it into English.",
        "Generate a list of timestamps containing the highlights of the last council meeting based on the transcripts and other webcast information. Translate everything to English.",
        "Show a summary of the Waste Collection Tax regulation 2026",
        "When is the next council meeting",
        "Wat is het armoedebeleid van de gemeente?",
    ]
    const [messages, setMessages] = useState<Message[]>([
        {
            id: 'welcome',
            content: 'Welcome! This is AiBabs, your personal AI assistant. How can I help you today?.' +
                '<ul>' +
                suggestions.map(suggestion => ` <li><a href="javascript:suggestFollowUp('${suggestion}')">${suggestion}</a></li>`).join('') +
                '</ul>',
            role: 'assistant',
            timeStamp: Date.now(),
        },
    ]);
    
    const [input, setInput] = useState('');
    const scrollRef = useRef<HTMLDivElement | null>(null);
    const intervalRef = useRef <number>(-1);
    const currentMessage = useRef<string>("");
    const [currentChat, setCurrentChat] = useState<Chat>({ 
        uniqueId: crypto.randomUUID(),
        name: "AiBabs",
        instructions: "", 
        messages: [] });

    useEffect(() => {
        // console.log("chat", chat);
        if (chat?.uniqueId) {
            let chat = {
                uniqueId: Helpers.generateKey(),
                name: "AiBabs",
                instructions: "",
                messages: [],
            };
            setCurrentChat(chat);
            setMessages(chat.messages);
        }
    }, [chat]);

    // Auto-scroll to the bottom whenever messages change
    useEffect(() => {
        const el = scrollRef.current;
        if (el) {
            el.scrollTop = el.scrollHeight;
        }
    }, [messages]);

    useEffect(() => {
        // Define the function on the window object
        (window as any).suggestFollowUp = (data: string) => {
            console.log("Followup clicked with data:", data);
            // You can call your React state setters or logic here
            setInput(data);
        };

        (window as any).jumpToTimestamp = (id: string, timestamp: string) => {
            console.log("jumpToTimestamp clicked with data:", id, timestamp);
            jumpToTimestamp(timestamp);
        };
        
        // Cleanup to prevent memory leaks or stale closures
        return () => {
            delete (window as any).suggestFollowUp;
            delete (window as any).jumpToTimestamp;
        };
    }, []);
    
    const gptStream = async (text: string) => {
        const response = await fetch(`${baseUrl}/stream/${currentChat.uniqueId}`, {
            // method: currentPrompt.promptId > 0 ? 'PUT' : 'POST',
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                "DeviceKey": Helpers.getGetOrCreateKey("sU6yh6NfhSPu8ilH")
            },
            body: JSON.stringify(text),
        });

        if (response.status !== 200) {
            alert("Error saving prompt: " + response.status);
            return;
        }

        if (response.body) {
            await streamReader(response.body);
        }
    }

    const streamReader= async (stream: ReadableStream<Uint8Array>) => {
        const reader = stream.getReader();
        if (!reader && reader != undefined) {
            await streamReader(reader);
        }
        interface Value {
            text: string;
            isThinking: boolean;
        }
        
        let message: Message =
            {
                id: `${Date.now()}`,
                content: "",
                role: 'assistant',
                timeStamp: Date.now(),
            };
        setMessages((prev) => [...prev, message]);
        
        let fullText = "";
        currentMessage.current = fullText;
        intervalRef.current = window.setInterval(() => {
            updateLastMessage()
        }, 100);
        
        let isThinking = false;
        while (true) {
            const { value, done } = await reader.read()
            if (done) {
                break;
            }
            if (value == null) {
                continue
            }
            
            let text = new TextDecoder().decode(value);
            let lines = text.split("\n");
            for (let line of lines) {
                // console.log(line);
                if (!line) {
                    continue;
                }
                
                const value = JSON.parse(line) as Value;
                if (!value.text) {
                    continue;
                }
                
                if (value.isThinking) {
                    // ![Alt text](https://assets.digitalocean.com/articles/alligator/boo.svg "a title")
                    fullText = "🤔 " + value.text;
                    // fullText = "![🤔](/thinking.gif) " + value.text;
                    // fullText = '<img src="/thinking.gif" alt="🤔" style="width: 32px;height: 32px;vertical-align: middle;" /> ' + value.text;
                } else if (isThinking && !value.isThinking) {
                    fullText = value.text
                } else {
                    fullText += value.text;
                }
                // console.log("fullText", fullText, value.isThinking, isThinking,);
                isThinking = value.isThinking;
                
                currentMessage.current = await MarkDown.parseMarkDown(fullText, false);
                // console.log("currentmessage", currentmessage.current);
            }
        }
        // stream reader done
        window.clearInterval(intervalRef.current);
        intervalRef.current = -1;
        
        // parse and set final result
        console.log("currentMessage", currentMessage.current);
        currentMessage.current = await MarkDown.parseMarkDown(fullText, false, true);
        updateLastMessage();

    }
    
    const updateLastMessage = () => {
        if (currentMessage == null || currentMessage.current == "") {
            return;
        }
        
        setMessages(prev => {
            if (prev.length === 0) return prev;

            const lastIndex = prev.length - 1;
            const last = prev[lastIndex];
            
            // if (last.id !== messageId) return prev; // Guard in case of race
            const updatedLast: Message = { ...last, content: currentMessage.current };

            return [...prev.slice(0, -1), updatedLast];
        });
    }
    
    const sendMessage = async () => {
        const text = input.trim();
        if (!text) {
            return;
        }
        const html= await MarkDown.parseMarkDown(text, false);
        const newMessage: Message = {
            id: `${Date.now()}`,
            content: html,
            role: 'user',
            timeStamp: Date.now()
        };

        setMessages((prev) => [...prev, newMessage]);
        setInput('');

        await gptStream(text);
    };

    const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = (e) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            sendMessage().catch(console.error);
        }
    };

    const jumpToTimestamp =  (timestamp: string) => {
        let message = {
            type: 'SET_PLAYER_TIME',
            time: timeToSeconds(timestamp)
        };

        const mediaElement = document.getElementById('mediaplayer') as HTMLIFrameElement;
        if (mediaElement) {
            mediaElement.contentWindow?.postMessage(message, "https://player.babscast.com");
        }
    }

    const timeToSeconds = (timeString: string): number => {
        if (!timeString) return 0;

        // Split by colon and convert segments to numbers
        const parts = timeString.split(':').map(Number);

        let totalSeconds = 0;

        // We process from right to left (seconds, then minutes, then hours)
        // parts.reverse() makes [SS, MM, HH]
        const reversedParts = parts.reverse();

        // Seconds
        totalSeconds += reversedParts[0] || 0;

        // Minutes (index 1) -> 60 seconds
        if (reversedParts[1]) {
            totalSeconds += reversedParts[1] * 60;
        }

        // Hours (index 2) -> 3600 seconds
        if (reversedParts[2]) {
            totalSeconds += reversedParts[2] * 3600;
        }

        return totalSeconds;
    };

    return (
        <Paper
            elevation={3}
            sx={{
                display: 'flex',
                flexDirection: 'column',
                // height: { xs: 'calc(100vh - 64px)', sm: '80vh' },
                height: 'calc(100vh - 190px)',
                width: '100%',
            }}
        >
            <Box
                ref={scrollRef}
                sx={{
                    flexGrow: 1,
                    overflowY: 'auto',
                    p: 2,
                    bgcolor: (theme) => theme.palette.background.default,
                }}
            >
                <Stack spacing={1.5}>
                    {messages.map((m) => (
                        <ChatBubble key={m.id} message={m} />
                    ))}
                </Stack>
            </Box>

            <Box
                sx={{
                    borderTop: (theme) => `1px solid ${theme.palette.divider}`,
                    p: 1,
                    display: 'flex',
                    gap: 1,
                    alignItems: 'baseline',
                }}
            >
                <TextareaAutosize
                    value={input}
                    onChange={(e) => setInput(e.target.value)}
                    onKeyDown={handleKeyDown}
                    placeholder="Type a message"
                    aria-label="minimum height"
                    minRows={1}
                    maxRows={5}
                    style={{ width: '100%', fontSize: '1.3rem' }}
                />
                <Button
                    variant="contained"
                    onClick={sendMessage}
                    style={{alignSelf: 'end'}}
                    disabled={!input.trim()}
                >
                    Send
                </Button>
            </Box>
        </Paper>
    );
};

export default ChatMessageList;